import { CircuitNode, ComponentType, ComponentTypeEnum } from '../types';

/**
 * How a component starts life on the canvas, and which of its values are
 * always shown on its tile.
 *
 * A component dragged from the generic library is created as a *custom*
 * component straight away — as if its defaults had been saved in the model
 * picker ("Save Custom") — so its properties are unambiguous from the start.
 * In particular a pump has exactly two modes: its **curve** (library model or
 * custom curve) or a **fixed flow**; there is no separate free "flow rate".
 */

export const DEFAULT_FIXED_FLOW_LPM = 30;

/** Values shown on the tile whenever they are set, not via a display checkbox. */
export const ALWAYS_SHOWN: Partial<Record<ComponentTypeEnum, string[]>> = {
  pump: ['fixed_flow_lpm'],
  heat_source: ['heat_input_kw'],
};

/** Pump properties that are superseded by the two pump modes. */
const LEGACY_PUMP_KEYS = ['flow_rate'];

export type PumpMode = 'fixed' | 'curve';

export function pumpMode(properties: Record<string, any> | undefined): PumpMode {
  return properties?.fixed_flow_lpm != null ? 'fixed' : 'curve';
}

/** Node data for a component dropped from the library. */
export function newNodeData(component: ComponentType): CircuitNode['data'] {
  const properties: Record<string, any> = { ...component.default_properties };
  if (component.id > 0) {
    return { component, custom_name: '', properties, display_properties: {} };
  }

  // Generic entry: start as a saved custom component with the defaults.
  const custom: ComponentType = {
    ...component,
    description: `Custom: ${component.name}`,
  };
  if (component.component_type === 'pump') {
    LEGACY_PUMP_KEYS.forEach(k => delete properties[k]);
    if (!Array.isArray(properties.custom_curve) && properties.fixed_flow_lpm == null) {
      properties.fixed_flow_lpm = DEFAULT_FIXED_FLOW_LPM;
    }
  }
  return { component: custom, custom_name: '', properties, display_properties: {} };
}

/**
 * Pumps saved before the two modes carried a free `flow_rate` next to (or
 * instead of) fixed flow. A generic pump without a curve was in effect a
 * fixed-flow pump, so that value becomes its fixed flow; everywhere else the
 * value was only the solver's starting guess and is dropped.
 */
export function migratePumpProperties(nodes: any[]): any[] {
  return nodes.map(n => {
    const component = n?.data?.component;
    const props = n?.data?.properties;
    if (component?.component_type !== 'pump' || !props || props.flow_rate === undefined) return n;
    const next = { ...props };
    const isGenericWithoutCurve = !(component.id > 0) && !Array.isArray(next.custom_curve);
    if (isGenericWithoutCurve && next.fixed_flow_lpm == null && typeof next.flow_rate === 'number') {
      next.fixed_flow_lpm = next.flow_rate;
    }
    delete next.flow_rate;
    const display = { ...(n.data.display_properties || {}) };
    delete display.flow_rate;
    return { ...n, data: { ...n.data, properties: next, display_properties: display } };
  });
}
